import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router';
import { AuthProvider } from '@context/AuthContext';
import { PermissionsProvider } from '@context/PermissionsContext';
import { OnboardingGateProvider } from '@context/OnboardingGateContext';
import { ThemeProvider } from '@context/ThemeContext';
import { ToastProvider } from '@context/ToastContext';
import { PageHeaderProvider } from '@context/PageHeaderContext';
import { TimezoneProvider } from '@context/TimezoneContext';
import { NotificationProvider } from '@context/NotificationContext';
import LoadingSpinner from '@components/common/atoms/LoadingSpinner';
import ProtectedRoute from '@/guards/ProtectedRoute';
import SettingsGuard from '@/guards/SettingsGuard';
import QueryParamGuard from '@/guards/QueryParamGuard';
import NotFoundPage from '@pages/NotFoundPage';
import LoginPage from '@pages/LoginPage';
import LandingPage from '@pages/LandingPage';
const SetInitialPasswordPage = lazy(() => import('@pages/SetInitialPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@pages/ForgotPasswordPage'));
const ChangePasswordPage = lazy(() => import('@pages/ChangePasswordPage'));
import AppShell from '@components/layout/AppShell';
import Card from '@components/common/organisms/AppCard';

// Lazy load pages for code splitting
const ProfilePage = lazy(() => import('@pages/ProfilePage'));
const DashboardPage = lazy(() => import('@pages/DashboardPage'));
const AttendancePage = lazy(() => import('@/features/attendance/pages/AttendancePage'));
const NotificationsPage = lazy(() => import('@pages/NotificationsPage'));
const AuthCallbackPage = lazy(() => import('@pages/AuthCallbackPage'));
const OnboardingWizardPage = lazy(() => import('@/features/onboarding/pages/OnboardingWizardPage'));

// Leave Management pages
const ApplyLeavePage = lazy(() => import('@/features/leave/pages/ApplyLeavePage'));
const LeaveHistoryPage = lazy(() => import('@/features/leave/pages/LeaveHistoryPage'));
const ApproverInboxPage = lazy(() => import('@/features/leave/pages/ApproverInboxPage'));

// WFH pages
const ApplyWfhPage = lazy(() => import('@/features/leave/pages/ApplyWfhPage'));
const WfhHistoryPage = lazy(() => import('@/features/leave/pages/WfhHistoryPage'));

// Offboarding (docs/OFFBOARDING_EXECUTION_PLAN.md)
const MyOffboardingPage = lazy(() => import('@/features/offboarding/pages/MyOffboardingPage'));
const InitiateOffboardingPage = lazy(() => import('@/features/offboarding/pages/InitiateOffboardingPage'));
const OffboardingApprovalsPage = lazy(() => import('@/features/offboarding/pages/OffboardingApprovalsPage'));
const OffboardingAdminPage = lazy(() => import('@/features/offboarding/pages/OffboardingAdminPage'));
const OffboardingRecordPage = lazy(() => import('@/features/offboarding/pages/OffboardingRecordPage'));

// Settings pages
const SettingsOverview = lazy(() => import('@pages/SettingsOverview'));
const ConfigurationOverview = lazy(() => import('@/features/configuration/pages/ConfigurationOverview'));
const PermissionsMatrix = lazy(() => import('@/features/configuration/pages/PermissionsMatrix'));
const PublicDocumentsScreen = lazy(() => import('@pages/PublicDocumentsScreen'));
const AnnouncementsScreen = lazy(() => import('@pages/AnnouncementsScreen'));
const SubscriptionScreen = lazy(() => import('@pages/SubscriptionScreen'));
const UserManagementScreen = lazy(() => import('@/features/employees/pages/UserManagementScreen'));
const OrgTreeScreen = lazy(() => import('@pages/OrgTreeScreen'));
const EmployeeDetailPage = lazy(() => import('@/features/employees/pages/EmployeeDetailPage'));
const CreateEmployeePage = lazy(() => import('@/features/employees/pages/CreateEmployeePage'));
const OnboardingDashboardPage = lazy(() => import('@/features/onboarding/pages/OnboardingDashboardPage'));
// Admin leave tools
const AdminLeaveTools = lazy(() => import('@/features/leave/pages/AdminLeaveTools'));

// KRA & Goals pages
const MyGoalsPage = lazy(() => import('@/features/goals/pages/MyGoalsPage'));
const TeamGoalsPage = lazy(() => import('@/features/goals/pages/TeamGoalsPage'));
const EmployeeGoalsPage = lazy(() => import('@/features/goals/pages/EmployeeGoalsPage'));
const KraManagementPage = lazy(() => import('@/features/goals/pages/KraManagementPage'));
const GoalWorkspacePage = lazy(() => import('@/features/goals/pages/GoalWorkspacePage'));

// Reports
const AttendanceReportPage = lazy(() => import('@pages/AttendanceReportPage'));

// Ticket Management pages
const TicketsPage = lazy(() => import('@/features/tickets/pages/TicketsPage'));
const RaiseTicketPage = lazy(() => import('@/features/tickets/pages/RaiseTicketPage'));
const TicketDetailPage = lazy(() => import('@/features/tickets/pages/TicketDetailPage'));
const EditTicketPage = lazy(() => import('@/features/tickets/pages/EditTicketPage'));

// Public pages (accessible to all roles)
const PublicDocumentsView = lazy(() => import('@pages/PublicDocumentsView'));

// Assets Management
const AssetsManagementScreen = lazy(() => import('@pages/AssetsManagementScreen'));
const AssetAllocationScreen = lazy(() => import('@pages/AssetAllocationScreen'));
const AssetFormScreen = lazy(() => import('@pages/AssetFormScreen'));

// Configuration master screens
const DepartmentsScreen = lazy(() => import('@/features/configuration/pages/DepartmentsScreen'));
const RolesScreen = lazy(() => import('@/features/configuration/pages/RolesScreen'));
const ShiftsScreen = lazy(() => import('@/features/configuration/pages/ShiftsScreen'));
const ProbationConfigPage = lazy(() => import('@/features/configuration/pages/ProbationConfigPage'));
const LeaveTypesScreen = lazy(() => import('@/features/configuration/pages/LeaveTypesScreen'));
const HolidaysScreen = lazy(() => import('@/features/configuration/pages/HolidaysScreen'));
const DesignationsScreen = lazy(() => import('@/features/configuration/pages/DesignationsScreen'));
const EmploymentTypesScreen = lazy(() => import('@/features/configuration/pages/EmploymentTypesScreen'));
const LocationsScreen = lazy(() => import('@/features/configuration/pages/LocationsScreen'));
const AssetTypesPage = lazy(() => import('@/features/configuration/pages/AssetTypesPage'));
const AssetCategoriesPage = lazy(() => import('@/features/configuration/pages/AssetCategoriesPage'));
const ExitInterviewQuestionsPage = lazy(() => import('@/features/configuration/pages/ExitInterviewQuestionsPage'));

// Coming Soon placeholder component
const ComingSoonPage: React.FC<{ pageName: string }> = ({ pageName }) => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Card className="text-center p-12 max-w-md">
      <div className="text-6xl mb-4">🚧</div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Coming Soon
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        {pageName} feature is currently under development and will be available soon.
      </p>
    </Card>
  </div>
);

// Singleton QueryClient — created once outside the component to survive re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TimezoneProvider>
            <ToastProvider>
              <NotificationProvider>
                <PermissionsProvider>
                  <OnboardingGateProvider>
                      <PageHeaderProvider>
                      <BrowserRouter>
                      {/* A public route called with parameters it does not declare is a 404, not a route */}
                      <QueryParamGuard>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                          {/* Public marketing landing page at the app root */}
                          <Route path="/" element={<LandingPage />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                          <Route path="/change-password" element={<ChangePasswordPage />} />
                          <Route path="/set-password" element={<SetInitialPasswordPage />} />
                          <Route path="/auth/callback" element={<AuthCallbackPage />} />
                          {/* Onboarding wizard - protected but without AppShell */}
                          <Route
                            path="/onboarding"
                            element={
                              <ProtectedRoute>
                                <OnboardingWizardPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Protected routes with AppShell layout */}
                          <Route
                            element={
                              <ProtectedRoute>
                                <AppShell />
                              </ProtectedRoute>
                            }
                          >
                            <Route path="/dashboard" element={<DashboardPage />} />
                            {/* Admin Leave Tools */}
                            <Route path="/admin/leave-tools" element={<ProtectedRoute requiredPermission={{ module: 'leave', action: 'manage' }}><AdminLeaveTools /></ProtectedRoute>} />
                            <Route path="/attendance" element={<AttendancePage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/payroll" element={<ComingSoonPage pageName="Payroll" />} />

                            {/* Leave Management routes */}
                            <Route path="/leave/apply" element={<ApplyLeavePage />} />
                            <Route path="/leave/history" element={<LeaveHistoryPage />} />
                            <Route path="/leave/approvals" element={<ProtectedRoute requiredPermission={{ module: 'leave', action: 'approve' }}><ApproverInboxPage /></ProtectedRoute>} />
                            <Route path="/leave" element={<LeaveHistoryPage />} />

                            {/* Offboarding */}
                            <Route path="/offboarding" element={<MyOffboardingPage />} />
                            <Route path="/offboarding/initiate" element={<InitiateOffboardingPage />} />
                            <Route path="/offboarding/approvals" element={<ProtectedRoute requiredPermission={{ module: 'offboarding', action: 'approve' }}><OffboardingApprovalsPage /></ProtectedRoute>} />
                            <Route path="/admin/offboarding" element={<ProtectedRoute requiredPermission={{ module: 'offboarding', action: 'view' }}><OffboardingAdminPage /></ProtectedRoute>} />
                            {/* Notification landing page — the API enforces who may see a record */}
                            <Route path="/offboarding/:id" element={<OffboardingRecordPage />} />

                            {/* WFH routes */}
                            <Route path="/wfh/apply" element={<ApplyWfhPage />} />
                            <Route path="/wfh" element={<WfhHistoryPage />} />
                            <Route path="/wfh/history" element={<WfhHistoryPage />} />

                            {/* KRA & Goals routes */}
                            <Route path="/goals" element={<MyGoalsPage />} />
                            <Route path="/goals/:goalId" element={<GoalWorkspacePage />} />
                            <Route
                              path="/team-goals"
                              element={
                                <ProtectedRoute requiredPermission={{ module: 'goals', action: 'view' }}>
                                  <TeamGoalsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/team-goals/:employeeId"
                              element={
                                <ProtectedRoute requiredPermission={{ module: 'goals', action: 'view' }}>
                                  <EmployeeGoalsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/kra"
                              element={
                                <ProtectedRoute requiredPermission={{ module: 'goals', action: 'manage' }}>
                                  <KraManagementPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route path="/performance" element={<ComingSoonPage pageName="Performance" />} />
                            <Route path="/documents" element={<PublicDocumentsView />} />
                            {/* Assets is limited to the Assets:AllowedEmails list in the API's
                                appsettings; the same list guards every /api/assets endpoint. */}
                            <Route path="/assets" element={<ProtectedRoute requiresAssetsAccess><AssetsManagementScreen /></ProtectedRoute>} />
                            <Route path="/assets/new" element={<ProtectedRoute requiresAssetsAccess><AssetFormScreen /></ProtectedRoute>} />
                            <Route path="/assets/:id/edit" element={<ProtectedRoute requiresAssetsAccess><AssetFormScreen /></ProtectedRoute>} />
                            <Route path="/assets/allocations" element={<ProtectedRoute requiresAssetsAccess><AssetAllocationScreen /></ProtectedRoute>} />
                            {/* Reports covers every employee's attendance, so it needs the
                                reports.manage permission, not just a hidden nav link.
                                The API endpoint enforces the same permission. */}
                            <Route path="/reports" element={<ProtectedRoute requiredPermission={{ module: 'reports', action: 'manage' }}><AttendanceReportPage /></ProtectedRoute>} />

                            {/* Ticket Management routes */}
                            <Route path="/ticket-management" element={<TicketsPage />} />
                            <Route path="/ticket-management/raise" element={<RaiseTicketPage />} />
                            <Route path="/ticket-management/:id/edit" element={<EditTicketPage />} />
                            <Route path="/ticket-management/:id" element={<TicketDetailPage />} />

                            {/* Settings routes - protected with SettingsGuard */}
                            <Route path="/settings" element={<SettingsGuard><SettingsOverview /></SettingsGuard>} />
                            <Route path="/settings/users" element={<SettingsGuard><UserManagementScreen /></SettingsGuard>} />
                            <Route path="/settings/users/new" element={<SettingsGuard><CreateEmployeePage /></SettingsGuard>} />
                            <Route path="/settings/users/:id" element={<SettingsGuard><EmployeeDetailPage /></SettingsGuard>} />
                            <Route path="/settings/org-tree" element={<SettingsGuard><OrgTreeScreen /></SettingsGuard>} />
                            <Route path="/settings/onboarding" element={<SettingsGuard><OnboardingDashboardPage /></SettingsGuard>} />
                            <Route path="/settings/configuration" element={<SettingsGuard><ConfigurationOverview /></SettingsGuard>} />
                            <Route path="/settings/public-documents" element={<SettingsGuard><PublicDocumentsScreen /></SettingsGuard>} />
                            <Route path="/settings/announcements" element={<SettingsGuard><AnnouncementsScreen /></SettingsGuard>} />
                            <Route path="/settings/subscriptions" element={<SettingsGuard><SubscriptionScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/departments" element={<SettingsGuard><DepartmentsScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/roles" element={<SettingsGuard><RolesScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/permissions" element={<SettingsGuard><PermissionsMatrix /></SettingsGuard>} />
                            <Route path="/settings/configuration/shifts" element={<SettingsGuard><ShiftsScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/probation" element={<SettingsGuard><ProbationConfigPage /></SettingsGuard>} />
                            <Route path="/settings/configuration/leave-types" element={<SettingsGuard><LeaveTypesScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/holidays" element={<SettingsGuard><HolidaysScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/designations" element={<SettingsGuard><DesignationsScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/employment-types" element={<SettingsGuard><EmploymentTypesScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/locations" element={<SettingsGuard><LocationsScreen /></SettingsGuard>} />
                            <Route path="/settings/configuration/asset-types" element={<SettingsGuard><AssetTypesPage /></SettingsGuard>} />
                            <Route path="/settings/configuration/asset-categories" element={<SettingsGuard><AssetCategoriesPage /></SettingsGuard>} />
                            <Route path="/settings/configuration/exit-interview-questions" element={<SettingsGuard><ExitInterviewQuestionsPage /></SettingsGuard>} />
                          </Route>

                          {/* Anything else is a dead end: show 404, never redirect an
                              unknown address onto the landing page or the dashboard. */}
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </Suspense>
                      </QueryParamGuard>
                      </BrowserRouter>
                      </PageHeaderProvider>
                  </OnboardingGateProvider>
                </PermissionsProvider>
              </NotificationProvider>
            </ToastProvider>
          </TimezoneProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
